import DOMPurify from 'dompurify';

const BLOCK_SELECTOR = 'p, div, li, ul, ol, h1, h2, h3, h4, h5, h6, blockquote, pre';

const isBlock = (el: Element) => el.matches(BLOCK_SELECTOR);
const hasText = (el: Element) => (el.textContent ?? '').replace(/\u00a0/g, ' ').trim().length > 0;

/**
 * Splits admin-authored rich text (Quill output) into one sanitized inline-HTML
 * string per line so the caller can render each line as its own row.
 *
 * Quill wraps every line in `<p>` (list items in `<ul>/<ol><li>`), so rendering
 * the raw HTML inline collapsed multiple lines into one. Empty lines such as
 * `<p><br></p>` are dropped. Content with no block wrapper comes back as a
 * single row. Links are sanitized by DOMPurify (no `javascript:` / `data:`
 * URLs — those anchors are unwrapped to plain text) and the rest are forced
 * to open in a new tab.
 */
export const splitRichTextRows = (html?: string | null): string[] => {
  if (!html || typeof window === 'undefined') {
    return [];
  }

  const doc = new DOMParser().parseFromString(DOMPurify.sanitize(html), 'text/html');
  doc.body.querySelectorAll('a[href]').forEach(anchor => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });
  // DOMPurify drops unsafe hrefs (`javascript:` etc.) but keeps the anchor;
  // unwrap it so a dead link doesn't render as a clickable one.
  doc.body.querySelectorAll('a:not([href])').forEach(anchor => anchor.replaceWith(...anchor.childNodes));

  const rows: string[] = [];
  const visit = (el: Element) => {
    const blockChildren = [...el.children].filter(isBlock);
    if (blockChildren.length > 0) {
      blockChildren.forEach(visit);
      return;
    }
    if (hasText(el)) {
      rows.push(el.innerHTML);
    }
  };

  const topLevel = [...doc.body.children];
  if (!topLevel.some(isBlock)) {
    return hasText(doc.body) ? [doc.body.innerHTML] : [];
  }

  topLevel.forEach(el => {
    if (isBlock(el)) {
      visit(el);
    } else if (hasText(el)) {
      rows.push(el.outerHTML);
    }
  });

  return rows;
};
